import React, { useState, useEffect } from 'react';
import { 
  FiX, 
  FiSave, 
  FiTag, 
  FiMapPin,
  FiEye,
  FiEyeOff,
  FiDroplet
} from 'react-icons/fi';
import { Zone, ZoneCategory } from '../../shared/types/zone';
import { zonesApi } from '../../services/ZonesApiService';
import './ZoneModal.css';

interface ZoneModalProps {
  zone?: Zone | null;
  onSave: (zone: Zone) => void;
  onCancel: () => void;
  loading?: boolean;
  drawingId: string;
  projectId: string;
}

const ZoneModal: React.FC<ZoneModalProps> = ({
  zone,
  onSave,
  onCancel,
  loading = false,
  drawingId,
  projectId
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3b82f6',
    category: '',
    tags: [] as string[],
    isActive: true
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [categories, setCategories] = useState<ZoneCategory[]>([]);
  const [newTag, setNewTag] = useState('');

  // Predefined colors
  const colorOptions = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4',
    '#84cc16', '#f97316', '#ec4899', '#6366f1', '#14b8a6', '#f43f5e'
  ];

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await zonesApi.getZoneCategories();
        setCategories(cats);
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Initialize form data when zone changes
  useEffect(() => {
    if (zone) {
      setFormData({
        name: zone.name,
        description: zone.description || '',
        color: zone.color,
        category: zone.category || '',
        tags: zone.tags || [],
        isActive: zone.isActive
      });
    } else {
      setFormData({
        name: '',
        description: '',
        color: '#3b82f6',
        category: '',
        tags: [],
        isActive: true
      });
    }
    setErrors({});
  }, [zone]);

  // Handle form input changes
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle tag addition
  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  // Handle tag removal
  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    const newErrors: { [key: string]: string } = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Zone name is required';
    }
    if (!formData.color) {
      newErrors.color = 'Zone color is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const zoneData: Partial<Zone> = {
        ...formData,
        drawingId,
        projectId,
        createdBy: zone?.createdBy || 'current-user',
        createdByName: zone?.createdByName || 'Current User',
        createdAt: zone?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        coordinates: zone?.coordinates || []
      };

      if (zone?.id) {
        zoneData.id = zone.id;
      }

      onSave(zoneData as Zone);
    } catch (error) {
      console.error('Failed to save zone:', error);
    }
  };

  return (
    <div className="zone-modal-overlay" onClick={onCancel}>
      <div className="zone-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="zone-modal-header">
          <h2 className="zone-modal-title">
            <FiMapPin size={20} />
            {zone ? 'Edit Zone' : 'Create New Zone'}
          </h2>
          <button
            className="zone-modal-close-btn"
            onClick={onCancel}
            disabled={loading}
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Form */}
        <form className="zone-modal-form" onSubmit={handleSubmit}>
          <div className="zone-form-grid">
            {/* Zone Name */}
            <div className="zone-form-group full-width">
              <label className="zone-form-label required">
                Zone Name
              </label>
              <input
                type="text"
                className={`zone-form-input ${errors.name ? 'error' : ''}`}
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter zone name"
                disabled={loading}
              />
              {errors.name && (
                <span className="zone-error-message">{errors.name}</span>
              )}
            </div>

            {/* Description */}
            <div className="zone-form-group full-width">
              <label className="zone-form-label">
                Description
              </label>
              <textarea
                className="zone-form-textarea"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter zone description (optional)"
                rows={3}
                disabled={loading}
              />
            </div>

            {/* Color Selection */}
            <div className="zone-form-group">
              <label className="zone-form-label required">
                <FiDroplet size={16} />
                Zone Color
              </label>
              <div className="zone-color-picker">
                {colorOptions.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`zone-color-option ${formData.color === color ? 'selected' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => handleInputChange('color', color)}
                    disabled={loading}
                    title={color}
                  />
                ))}
              </div>
              {errors.color && (
                <span className="zone-error-message">{errors.color}</span>
              )}
            </div>

            {/* Category */}
            <div className="zone-form-group">
              <label className="zone-form-label">
                <FiTag size={16} />
                Category
              </label>
              <select
                className="zone-form-select"
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                disabled={loading}
              >
                <option value="">Select category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Tags */}
            <div className="zone-form-group full-width">
              <label className="zone-form-label">
                Tags
              </label>
              <div className="zone-tags-input">
                <div className="zone-tags-list">
                  {formData.tags.map((tag, index) => (
                    <span key={index} className="zone-tag">
                      {tag}
                      <button
                        type="button"
                        className="zone-tag-remove"
                        onClick={() => handleRemoveTag(tag)}
                        disabled={loading}
                      >
                        <FiX size={12} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="zone-tag-input">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    placeholder="Add tag..."
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="zone-tag-add-btn"
                    onClick={handleAddTag}
                    disabled={loading || !newTag.trim()}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Active Status */}
            <div className="zone-form-group">
              <label className="zone-form-label">
                <FiEye size={16} />
                Status
              </label>
              <div className="zone-status-toggle">
                <button
                  type="button"
                  className={`zone-status-btn ${formData.isActive ? 'active' : 'inactive'}`}
                  onClick={() => handleInputChange('isActive', !formData.isActive)}
                  disabled={loading}
                >
                  {formData.isActive ? <FiEye size={16} /> : <FiEyeOff size={16} />}
                  {formData.isActive ? 'Active' : 'Inactive'}
                </button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="zone-modal-actions">
            <button
              type="button"
              className="zone-btn-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="zone-btn-primary"
              disabled={loading}
            >
              {loading ? (
                <div className="zone-loading-spinner" />
              ) : (
                <>
                  <FiSave size={16} />
                  {zone ? 'Update Zone' : 'Create Zone'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ZoneModal;
